import { Injectable } from '@angular/core';
import { CustomerServiceIssueType, CustomerServiceIssueTypeDto } from '../../models/customer-service/customer-service-issue-type.model';
import { PagedResultDto } from '../../models/table.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerServiceIssueTypeMapper {

  // ================= Normal Functions =================

  /**
   * Parse a date string into a Date object safely
   */
  private parseDate(val: string | null | undefined): Date | null {
    if (!val) return null;
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Translates category code to readable Thai label
   */
  translateCategory(category: string): string {
    switch (category) {
      case 'refund': return 'คืนเงิน';
      case 'complaint': return 'ข้อร้องเรียน';
      case 'general': return 'สอบถามทั่วไป';
      case 'technical': return 'ปัญหาทางเทคนิค';
      case 'payment_sync': return 'ปรับยอดชำระ';
      case 'registration': return 'สมัครสมาชิก/ลงทะเบียน';
      default: return category || '-';
    }
  }

  /**
   * Translates resolution mode code to readable Thai label
   */
  translateResolutionMode(mode: string): string {
    switch (mode) {
      case 'refund': return 'คืนเงิน';
      case 'payment_fix': return 'แก้ไขการชำระเงิน/ปรับยอด';
      case 'registration_fix': return 'แก้ไขข้อมูลลงทะเบียน';
      case 'inquiry': return 'ตอบข้อซักถาม';
      case 'complaint': return 'จัดการข้อร้องเรียน';
      default: return mode || '-';
    }
  }

  /**
   * Maps a CustomerServiceIssueType DTO into UI Model
   */
  mapIssueType(dto: CustomerServiceIssueTypeDto): CustomerServiceIssueType {
    const raw = dto as CustomerServiceIssueTypeDto & Record<string, any>;
    return {
      id: dto.id ?? raw['Id'] ?? 0,
      code: dto.code ?? raw['Code'] ?? '',
      name: dto.name ?? raw['Name'] ?? '',
      category: dto.category ?? raw['Category'] ?? '',
      resolutionMode: dto.resolutionMode ?? raw['ResolutionMode'] ?? '',
      isOnline: dto.isOnline ?? raw['IsOnline'] ?? false,
      active: dto.active ?? raw['Active'] ?? false,
      customerSelectable: dto.customerSelectable ?? raw['CustomerSelectable'] ?? false,
      requiresRefund: dto.requiresRefund ?? raw['RequiresRefund'] ?? false,
      requiresApproval: dto.requiresApproval ?? raw['RequiresApproval'] ?? false,
      sortOrder: dto.sortOrder ?? raw['SortOrder'] ?? 0,
      defaultSlaHours: dto.defaultSlaHours ?? raw['DefaultSlaHours'] ?? 0,
      description: dto.description ?? raw['Description'] ?? '',
      createDate: this.parseDate(dto.createDate ?? raw['CreateDate']),
      linkedCaseCount: dto.linkedCaseCount ?? raw['LinkedCaseCount'] ?? 0,
      canEditCode: dto.canEditCode ?? raw['CanEditCode'] ?? (dto.linkedCaseCount === 0)
    };
  }

  /**
   * Maps table response DTO into PagedResultDto of CustomerServiceIssueType UI Model
   */
  mapIssueTypeTable(dto: any): PagedResultDto<CustomerServiceIssueType> {
    const raw = dto as Record<string, any>;
    const list = (dto.data ?? raw['Data'] ?? []) as CustomerServiceIssueTypeDto[];
    const total = dto.total ?? raw['Total'] ?? 0;

    return {
      data: list.map(item => this.mapIssueType(item)),
      total: total
    };
  }

  /**
   * Creates an empty Issue Type model for creation form
   */
  createEmptyIssueType(): Partial<CustomerServiceIssueType> {
    return {
      id: 0,
      code: '',
      name: '',
      category: '',
      resolutionMode: '',
      isOnline: true,
      active: true,
      customerSelectable: true,
      requiresRefund: false,
      requiresApproval: false,
      sortOrder: 10,
      defaultSlaHours: 24,
      description: '',
      linkedCaseCount: 0,
      canEditCode: true
    };
  }

  /**
   * Maps form values into API request payload for insertion or update
   */
  mapPayload(form: Partial<CustomerServiceIssueType>, userId: number, menuId: number): any {
    return {
      userId,
      menuId,
      code: form.code?.trim().toLowerCase() ?? '',
      name: form.name?.trim() ?? '',
      category: form.category ?? '',
      resolutionMode: form.resolutionMode ?? '',
      isOnline: !!form.isOnline,
      active: !!form.active,
      customerSelectable: !!form.customerSelectable,
      requiresRefund: !!form.requiresRefund,
      requiresApproval: !!form.requiresApproval,
      sortOrder: Number(form.sortOrder ?? 10),
      defaultSlaHours: Number(form.defaultSlaHours ?? 24),
      description: form.description?.trim() ?? ''
    };
  }
}
